// The workspace's places beside the conversation: Library (everything the EmCog made), Schedule (one timeline of what's
// coming), Activity (his own time, as it happened) and Town. Each reads /api/workspace/* with the PIN token.
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { BenchView, WorkbenchContext } from '../../contexts/WorkbenchContext';
import ActivityPanel from '../ActivityPanel';
import { openLinkFromDesktop } from '../../utils/desktop';
import AddToSchedule, { ScheduleDetail } from './AddToSchedule';

export function useWorkspaceData<T>(path: string, refreshMs = 60_000): { data: T | null; error: string | null; reload: () => void } {
  const { token } = useAuth();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      const res = await fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`Couldn't load this (${res.status}).`);
      setData(await res.json()); setError(null);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }, [path, token]);
  useEffect(() => { reload(); const t = setInterval(reload, refreshMs); return () => clearInterval(t); }, [reload, refreshMs]);
  // Something was just added from the workspace: show it now, not at the next refresh.
  useEffect(() => {
    const evs = ['emcog:schedule-changed', 'emcog:workspace-changed'];
    evs.forEach(e => window.addEventListener(e, reload));
    return () => evs.forEach(e => window.removeEventListener(e, reload));
  }, [reload]);
  return { data, error, reload };
}

/** A change through /api/workspace with the token; throws the server's words, and tells the rest of the workspace. */
export function useWorkspaceCall(): (path: string, method?: string, body?: any) => Promise<any> {
  const { token } = useAuth();
  return useCallback(async (path: string, method = 'POST', body?: any) => {
    const res = await fetch(path, { method, headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
    const out = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(out.error || out.message || `That didn't work (${res.status}).`);
    window.dispatchEvent(new Event('emcog:workspace-changed'));
    return out;
  }, [token]);
}

/** Delete a Library piece for good (asks first). The workbench closes if it had that piece open. */
export function useLibraryDelete(): (item: { id: string; title: string; by?: string; kind?: string }) => void {
  const bench = useContext(WorkbenchContext);
  const call = useWorkspaceCall();
  return (item) => bench.confirm({
    title: `Delete "${item.title}"?`,
    body: `It's gone for good — the file${item.kind === 'site' || item.kind === 'deck' || item.kind === 'anim' ? ', the page and its link' : ''} and its place on any project. ${bench.name} ${item.by === 'yours' ? 'knows you deleted it' : 'knows it was your call'}.`,
    action: 'Delete',
    run: async () => {
      await call(`/api/workspace/library/${encodeURIComponent(item.id)}`, 'DELETE');
      window.dispatchEvent(new CustomEvent('emcog:library-deleted', { detail: { id: item.id } }));
      bench.note(`Deleted "${item.title}"`);
    }
  });
}

export const when = (at: number): string => {
  const d = new Date(at), now = new Date();
  const days = Math.round((new Date(now.toDateString()).getTime() - new Date(d.toDateString()).getTime()) / 86_400_000);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (days === 0) return `Today, ${time}`;
  if (days === 1) return `Yesterday, ${time}`;
  if (days > 1 && days < 7) return `${d.toLocaleDateString([], { weekday: 'short' })}, ${time}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

// ── Library ─────────────────────────────────────────────────────────────────
interface LibItem { id: string; kind: string; view: BenchView; title: string; at: number; by: 'own' | 'for_you' | 'yours'; pageUrl?: string; fileName?: string; bytes?: number; inProgress?: boolean; words?: number; about: string }
const KIND_NAME: Record<string, string> = { doc: 'Documents', deck: 'Presentations', site: 'Websites', anim: 'Animations', song: 'Songs', image: 'Images', svg: 'Vector art', story: 'Stories', research: 'Research', code: 'Code', video: 'Videos', upload: 'Your files' };
const KIND_TAG: Record<string, string> = { doc: 'DOC', deck: 'DECK', site: 'SITE', anim: 'ANIM', song: 'SONG', image: 'IMAGE', svg: 'SVG', story: 'STORY', research: 'FIND', code: 'CODE', video: 'VIDEO', upload: 'YOURS' };
const BY: Record<string, string> = { own: 'Own work', for_you: 'Made for you', yours: 'You sent it' };

export const LibraryPlace: React.FC = () => {
  const bench = useContext(WorkbenchContext);
  const { data, error } = useWorkspaceData<{ items: LibItem[] }>('/api/workspace/library');
  const remove = useLibraryDelete();
  const call = useWorkspaceCall();
  // 🗂️ Picking several, like a photos app (Oct 30 2026): Select, tap the cards (shift-click picks a run), delete them at once.
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const lastPick = useRef<number | null>(null);
  const [filter, setFilter] = useState('all');
  const [who, setWho] = useState('all');
  const [query, setQuery] = useState('');
  const items = useMemo(() => data?.items || [], [data]);
  const kinds = useMemo(() => Object.keys(KIND_NAME).filter(k => items.some(i => i.kind === k)), [items]);
  // Whose it is — their own work, made for you, or yours — offered once there's more than one.
  const whos = useMemo(() => Object.keys(BY).filter(b => items.some(i => i.by === b)), [items]);
  const shown = items.filter(i => (filter === 'all' || i.kind === filter) && (who === 'all' || i.by === who) && (!query || i.title.toLowerCase().includes(query.toLowerCase())));
  const open = (i: LibItem) => bench.open?.({ title: i.title, view: i.view, libraryId: i.id, ...(i.pageUrl ? { url: i.pageUrl } : {}), fileName: i.fileName, meta: `${BY[i.by]} · ${when(i.at)}` });
  const stopSelecting = useCallback(() => { setSelecting(false); setPicked(new Set()); lastPick.current = null; }, []);
  useEffect(() => {
    if (!selecting) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') stopSelecting(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [selecting, stopSelecting]);
  // A piece deleted or gone elsewhere is no longer picked.
  useEffect(() => { setPicked(p => { const ids = new Set(items.map(i => i.id)); const next = new Set(Array.from(p).filter(id => ids.has(id))); return next.size === p.size ? p : next; }); }, [items]);
  const toggle = (index: number, shift: boolean) => {
    const id = shown[index].id;
    setPicked(p => {
      const next = new Set(p);
      if (shift && lastPick.current !== null) {
        const [a, b] = [Math.min(lastPick.current, index), Math.max(lastPick.current, index)];
        for (let k = a; k <= b; k++) next.add(shown[k].id);
      } else if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    lastPick.current = index;
  };
  const allShownPicked = shown.length > 0 && shown.every(i => picked.has(i.id));
  const deletePicked = () => {
    const chosen = items.filter(i => picked.has(i.id));
    if (!chosen.length) return;
    const names = chosen.slice(0, 5).map(i => `"${i.title}"`).join(', ') + (chosen.length > 5 ? ` and ${chosen.length - 5} more` : '');
    bench.confirm({
      title: `Delete ${chosen.length} piece${chosen.length === 1 ? '' : 's'}?`,
      body: `${names} — gone for good: the files${chosen.some(i => ['site', 'deck', 'anim'].includes(i.kind)) ? ', pages and links' : ''} and their places on any project. ${bench.name} hears about it once, as your call.`,
      action: `Delete ${chosen.length}`,
      run: async () => {
        const out = await call('/api/workspace/library/delete', 'POST', { ids: chosen.map(i => i.id) });
        for (const d of out.deleted || []) window.dispatchEvent(new CustomEvent('emcog:library-deleted', { detail: { id: d.id } }));
        const n = (out.deleted || []).length, f = (out.failed || []).length;
        bench.note(`Deleted ${n} piece${n === 1 ? '' : 's'}${f ? ` — ${f} couldn't be (${(out.failed || []).slice(0, 2).map((x: any) => x.error).join('; ')})` : ''}`);
        stopSelecting();
      }
    });
  };
  return (
    <div className="ws-wrap">
      <div className="ws-toolbar">
        <div className="ws-filters" role="group" aria-label="Filter by kind">
          <button aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>All</button>
          {kinds.map(k => <button key={k} aria-pressed={filter === k} onClick={() => setFilter(k)}>{KIND_NAME[k]}</button>)}
        </div>
        {whos.length > 1 && (
          <div className="ws-filters ws-filters-who" role="group" aria-label="Filter by whose it is">
            <button aria-pressed={who === 'all'} onClick={() => setWho('all')}>Everyone's</button>
            {whos.map(b => <button key={b} aria-pressed={who === b} onClick={() => setWho(b)}>{BY[b]}</button>)}
          </div>
        )}
        <label className="ws-search"><span className="ws-sr">Search the library</span><input id="ws-lib-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search titles…" /></label>
        {items.length > 0 && (selecting
          ? <div className="ws-select-bar" role="group" aria-label="Selected pieces">
              <span className="ws-select-count">{picked.size} selected</span>
              <button className="ws-btn" onClick={() => setPicked(allShownPicked ? new Set(Array.from(picked).filter(id => !shown.some(i => i.id === id))) : new Set([...Array.from(picked), ...shown.map(i => i.id)]))}>{allShownPicked ? 'Unselect shown' : 'Select all shown'}</button>
              <button className="ws-btn ws-danger" disabled={!picked.size} onClick={deletePicked}>Delete{picked.size ? ` ${picked.size}` : ''}</button>
              <button className="ws-btn" onClick={stopSelecting}>Done</button>
            </div>
          : <button className="ws-btn" onClick={() => setSelecting(true)} title="Pick several pieces to delete at once">Select</button>)}
      </div>
      {error && <div className="ws-empty">{error}</div>}
      {!error && data && !shown.length && <div className="ws-empty">{items.length ? 'Nothing matches.' : 'Nothing here yet — everything made shows up here.'}</div>}
      <div className="ws-grid">
        {shown.map((i, index) => (
          <div key={i.id} className={`ws-card-wrap${selecting ? ' ws-selecting' : ''}${picked.has(i.id) ? ' ws-picked' : ''}`}>
            <button className="ws-card" aria-pressed={selecting ? picked.has(i.id) : undefined} onClick={e => (selecting ? toggle(index, e.shiftKey) : open(i))}>
              <div className={`ws-thumb ws-tk-${i.kind}`}><span className="ws-tag">{KIND_TAG[i.kind] || 'FILE'}</span>{i.inProgress && <span className="ws-pill ws-pill-live">In progress</span>}</div>
              <h3>{i.title}</h3>
              <div className="ws-card-meta"><span>{BY[i.by]}</span><span>{when(i.at)}</span></div>
            </button>
            {selecting
              ? <span className="ws-card-check" aria-hidden="true">{picked.has(i.id) ? '✓' : ''}</span>
              : <button className="ws-card-x" aria-label={`Delete "${i.title}"`} title="Delete" onClick={() => remove(i)}>✕</button>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Schedule ────────────────────────────────────────────────────────────────
interface SchedItem { id: string; at: number; title: string; kind: 'calendar' | 'reminder' | 'promise' | 'task' | 'research' | 'town'; recurring?: string; forWhom?: 'person' | 'emcog' }
const SKIND: Record<string, string> = { calendar: 'Calendar', reminder: 'Reminders', promise: 'Promises', task: 'Scheduled tasks', research: 'Research jobs', town: 'Town' };

export const SchedulePlace: React.FC<{ emcogName: string }> = ({ emcogName }) => {
  const bench = useContext(WorkbenchContext);
  const call = useWorkspaceCall();
  const [days, setDays] = useState(7);
  const { data, error } = useWorkspaceData<{ items: SchedItem[] }>(`/api/workspace/schedule?days=${days}`);
  // 🔎 An entry opens to everything it is — changeable when it's a reminder, a promise or a routine (Oct 31 2026).
  const [open, setOpen] = useState<{ item: SchedItem; detail: ScheduleDetail } | null>(null);
  const openItem = async (e: SchedItem) => {
    if (e.kind === 'town' || e.kind === 'research') return;
    try {
      const r = await call(`/api/workspace/schedule/${e.kind}/${encodeURIComponent(e.id)}`, 'GET');
      if (r?.item) setOpen({ item: e, detail: { ...r.item, kind: e.kind } });
      else bench.note(`"${e.title}" is no longer on the schedule`);
    } catch (err) { bench.note(err instanceof Error ? err.message : String(err)); }
  };
  // Plans to meet in town are made with a neighbor, so they're called off in the chat, not here.
  const remove = (e: SchedItem) => bench.confirm({
    title: `Take "${e.title}" off the schedule?`,
    body: e.kind === 'promise' ? `${emcogName} won't do it${e.recurring ? ' again' : ''}, and knows you took it off.`
      : e.kind === 'research' ? `The research job stops coming round. ${emcogName} knows you cancelled it.`
      : `${e.recurring ? `Every repeat goes, not just this one. ` : ''}${emcogName} knows you took it off.`,
    action: 'Take it off',
    run: async () => { await call(`/api/workspace/schedule/${e.kind}/${encodeURIComponent(e.id)}`, 'DELETE'); window.dispatchEvent(new Event('emcog:schedule-changed')); bench.note(`Took "${e.title}" off the schedule`); }
  });
  const groups = useMemo(() => {
    const out: Array<{ label: string; sub: string; items: SchedItem[] }> = [];
    for (const it of data?.items || []) {
      const d = new Date(it.at), today = new Date();
      const diff = Math.round((new Date(d.toDateString()).getTime() - new Date(today.toDateString()).getTime()) / 86_400_000);
      const label = diff <= 0 ? 'Today' : diff === 1 ? 'Tomorrow' : d.toLocaleDateString([], { weekday: 'long' });
      const sub = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
      const g = out.find(x => x.label === label) || (out.push({ label, sub, items: [] }), out[out.length - 1]);
      g.items.push(it);
    }
    return out;
  }, [data]);
  return (
    <div className="ws-wrap">
      <div className="ws-toolbar">
        <div className="ws-legend">{Object.entries(SKIND).map(([k, l]) => <span key={k}><i className={`ws-sk-${k}`} />{l}</span>)}</div>
        <div className="ws-filters" role="group" aria-label="How far ahead">
          {[7, 14, 31].map(n => <button key={n} aria-pressed={days === n} onClick={() => setDays(n)}>{n === 31 ? 'Month' : `${n} days`}</button>)}
        </div>
      </div>
      {error && <div className="ws-empty">{error}</div>}
      {!error && data && !groups.length && <div className="ws-empty">Nothing coming up. Use + Add to put something on it, or ask in the chat.</div>}
      {groups.map(g => (
        <div className="ws-day" key={g.label + g.sub}>
          <h3>{g.label}<span>{g.sub}</span></h3>
          <div className="ws-list">
            {g.items.map(e => (
              <div className="ws-ev" key={e.kind + e.id + e.at}>
                <span className="ws-mono">{new Date(e.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                <span className={`ws-ev-bar ws-sk-${e.kind}`} />
                <button type="button" className="ws-ev-tt ws-ev-open" disabled={e.kind === 'town' || e.kind === 'research'} title={e.kind === 'town' || e.kind === 'research' ? undefined : 'Open it — see it whole, change it'} onClick={() => openItem(e)}>
                  <b>{e.title}</b><span>{SKIND[e.kind]}{e.kind === 'task' && e.forWhom ? (e.forWhom === 'emcog' ? ` · for ${emcogName} to do` : ' · reminds you') : ''}{e.recurring ? ` · ${e.recurring}` : ''}</span>
                </button>
                {e.kind === 'town'
                  ? <span className="ws-ev-x-gap" title={`Plans with a neighbor — ask ${emcogName} in the chat to call it off`} />
                  : <button className="ws-todo-x" aria-label={`Take "${e.title}" off the schedule`} title="Take it off the schedule" onClick={() => remove(e)}>✕</button>}
              </div>
            ))}
          </div>
        </div>
      ))}
      {open && <AddToSchedule emcogName={emcogName} existing={open.detail} onClose={() => setOpen(null)} onAdded={t => bench.note(t)} onRemove={() => remove(open.item)} />}
    </div>
  );
};

// ── Activity ────────────────────────────────────────────────────────────────
interface ActItem { at: number; kind: 'work' | 'rest' | 'town'; title: string; detail: string }

export const ActivityPlace: React.FC<{ emcogName: string }> = ({ emcogName }) => {
  const [hours, setHours] = useState(24);
  const { data, error } = useWorkspaceData<{ items: ActItem[] }>(`/api/workspace/activity?hours=${hours}`);
  return (
    <div className="ws-wrap ws-narrow">
      <div className="ws-toolbar">
        <p className="ws-lede">What {emcogName} did with their own time — the record, not a summary.</p>
        <div className="ws-filters" role="group" aria-label="How far back">
          {[24, 72, 168].map(n => <button key={n} aria-pressed={hours === n} onClick={() => setHours(n)}>{n === 24 ? 'Today' : n === 72 ? '3 days' : 'Week'}</button>)}
        </div>
      </div>
      {error && <div className="ws-empty">{error}</div>}
      {!error && data && !data.items.length && <div className="ws-empty">Nothing on the record for this stretch.</div>}
      <div className="ws-acts">
        {(data?.items || []).map((a, i) => (
          <div className="ws-act" key={a.at + ':' + i}>
            <span className="ws-mono ws-act-t">{when(a.at)}</span>
            <span className={`ws-act-n ws-ak-${a.kind}`} />
            <p>{a.title}{a.detail && <><br /><span>{a.detail}</span></>}</p>
          </div>
        ))}
      </div>
      <details className="ws-more"><summary>Connector log and lessons learned</summary><ActivityPanel /></details>
    </div>
  );
};

// ── Lists ───────────────────────────────────────────────────────────────────
// Two sides, never mixed (Oct 26 2026): yours, and the EmCog's own — intentions and rhythms. A rhythm has a cadence
// and no clock time; ticking it marks this round, and it comes due again on its own.
type Owner = 'person' | 'self';
type Cadence = 'daily' | 'every few days' | 'weekly' | 'every two weeks' | 'monthly';
interface ListRow { id: string; text: string; done: boolean; priority: string | null; due: number | null; notes: string | null; at: number; doneAt: number | null; owner: Owner; cadence: Cadence | null; lastDoneAt: number | null; nextAt: number | null }
interface ListReminder { id: string; at: number; label: string; recurring: boolean }
interface TodoList { key: string; display: string; owner: Owner; open: ListRow[]; done: ListRow[]; reminders?: ListReminder[] }
const CADENCES: Array<[Cadence | '', string]> = [['', 'Once'], ['daily', 'Daily'], ['every few days', 'Every few days'], ['weekly', 'Weekly'], ['every two weeks', 'Every two weeks'], ['monthly', 'Monthly']];
const CADENCE_NAME = Object.fromEntries(CADENCES) as Record<string, string>;

/** "today" / "yesterday" / "tomorrow" / "Tue" / "Oct 20" — a day, near or far, past or ahead. */
const dayWord = (at: number): string => {
  const d = new Date(at);
  const days = Math.round((new Date(d.toDateString()).getTime() - new Date(new Date().toDateString()).getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === -1) return 'yesterday';
  if (days === 1) return 'tomorrow';
  if (Math.abs(days) < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

// ⏰ A reminder on one of your lists (Oct 27 2026 — Ryan: "in the list card I could click a 'create reminder' and then
// make a one off or recurring reminder about what's in the list and work through it"). Once, or repeating; when it
// comes, the EmCog reads what's open on the list right then and offers to go through it with you.
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const pad2 = (n: number) => String(n).padStart(2, '0');

const ListReminderForm: React.FC<{ list: TodoList; emcogName: string; onClose: () => void }> = ({ list, emcogName, onClose }) => {
  const bench = useContext(WorkbenchContext);
  const call = useWorkspaceCall();
  const soon = useMemo(() => { const d = new Date(Date.now() + 60 * 60 * 1000); d.setMinutes(0, 0, 0); return d; }, []);
  const [kind, setKind] = useState<'once' | 'repeat'>('once');
  const [date, setDate] = useState(`${soon.getFullYear()}-${pad2(soon.getMonth() + 1)}-${pad2(soon.getDate())}`);
  const [time, setTime] = useState(`${pad2(soon.getHours())}:${pad2(soon.getMinutes())}`);
  const [every, setEvery] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [days, setDays] = useState<number[]>([soon.getDay()]);
  const [dayOfMonth, setDayOfMonth] = useState(String(soon.getDate()));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => { first.current?.focus(); const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', esc); return () => window.removeEventListener('keydown', esc); }, [onClose]);
  const toggleDay = (d: number) => setDays(ds => ds.includes(d) ? ds.filter(x => x !== d) : [...ds, d].sort((a, b) => a - b));
  const id = `ws-rem-${list.key}`;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{2}:\d{2}$/.test(time)) { setError('Pick a time.'); return; }
    let body: Record<string, unknown>;
    if (kind === 'once') {
      const at = new Date(`${date}T${time}`);
      if (isNaN(at.getTime())) { setError('Pick a date and a time.'); return; }
      if (at.getTime() <= Date.now()) { setError('That time has already passed.'); return; }
      body = { repeat: 'once', date, time };
    } else if (every === 'weekly') {
      if (!days.length) { setError('Pick at least one day.'); return; }
      body = { repeat: 'weekly', days, time };
    } else if (every === 'monthly') {
      const d = Number(dayOfMonth);
      if (!Number.isInteger(d) || d < 1 || d > 31) { setError('Pick a day of the month from 1 to 31.'); return; }
      body = { repeat: 'monthly', dayOfMonth: d, time };
    } else {
      body = { repeat: 'daily', time };
    }
    setBusy(true); setError(null);
    try {
      const r = await call(`/api/workspace/lists/${encodeURIComponent(list.key)}/reminders`, 'POST', body);
      window.dispatchEvent(new Event('emcog:schedule-changed'));
      bench.note(r.alreadyExisted ? `Your ${list.display} list already has that reminder` : `Reminder set — ${r.label}. ${emcogName} knows`);
      onClose();
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); setBusy(false); }
  };

  return (
    <div className="ws-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="ws-modal" onSubmit={submit} aria-labelledby={`${id}-title`}>
        <h3 id={`${id}-title`}>Remind me about {list.display}</h3>
        <p className="ws-modal-lede">When it comes, {emcogName} reads what's still open on the list right then and offers to go through it with you.</p>
        <div className="ws-seg" role="radiogroup" aria-label="Once or repeating">
          <button type="button" role="radio" ref={first} aria-checked={kind === 'once'} onClick={() => setKind('once')}>Once</button>
          <button type="button" role="radio" aria-checked={kind === 'repeat'} onClick={() => setKind('repeat')}>Repeating</button>
        </div>
        {kind === 'once' ? (
          <div className="ws-row2">
            <label htmlFor={`${id}-date`}>Date<input id={`${id}-date`} type="date" value={date} onChange={e => setDate(e.target.value)} /></label>
            <label htmlFor={`${id}-time`}>Time<input id={`${id}-time`} type="time" value={time} onChange={e => setTime(e.target.value)} /></label>
          </div>
        ) : (
          <>
            <div className="ws-row2">
              <label htmlFor={`${id}-every`}>Repeats
                <select id={`${id}-every`} value={every} onChange={e => setEvery(e.target.value as 'daily' | 'weekly' | 'monthly')}>
                  <option value="daily">Every day</option>
                  <option value="weekly">On chosen weekdays</option>
                  <option value="monthly">Monthly on a day</option>
                </select>
              </label>
              <label htmlFor={`${id}-rtime`}>Time<input id={`${id}-rtime`} type="time" value={time} onChange={e => setTime(e.target.value)} /></label>
            </div>
            {every === 'weekly' && (
              <div className="ws-filters ws-rem-days" role="group" aria-label="Which days">
                {WEEKDAYS.map((n, d) => <button type="button" key={n} aria-pressed={days.includes(d)} onClick={() => toggleDay(d)}>{n}</button>)}
              </div>
            )}
            {every === 'monthly' && (
              <label htmlFor={`${id}-dom`}>Day of the month
                <input id={`${id}-dom`} type="number" min={1} max={31} value={dayOfMonth} onChange={e => setDayOfMonth(e.target.value)} />
                <span className="ws-rem-hint">A month without that day uses its last day.</span>
              </label>
            )}
          </>
        )}
        {error && <div className="ws-form-error" role="alert">{error}</div>}
        <div className="ws-modal-acts">
          <button type="button" className="ws-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="ws-btn ws-primary" disabled={busy}>{busy ? 'Setting…' : 'Set reminder'}</button>
        </div>
      </form>
    </div>
  );
};

const ListCard: React.FC<{ list: TodoList; emcogName: string; act: (path: string, method: string, body?: any) => Promise<void> }> = ({ list, emcogName, act }) => {
  const bench = useContext(WorkbenchContext);
  const mine = list.owner === 'self';
  const title = list.display;
  const whose = mine ? `${emcogName}'s ${title} list` : `your ${title} list`;
  const count = list.open.length + list.done.length;
  const removeList = () => bench.confirm({
    title: `Delete ${whose}?`,
    body: `All ${count} item${count === 1 ? '' : 's'} on it go, done ones and rhythms too.${list.reminders?.length ? ` Its reminder${list.reminders.length === 1 ? '' : 's'} stay${list.reminders.length === 1 ? 's' : ''} on the Schedule until you take ${list.reminders.length === 1 ? 'it' : 'them'} off there.` : ''} ${emcogName} knows you deleted it.`,
    action: 'Delete the list',
    run: async () => { await act(`/api/workspace/lists/${encodeURIComponent(list.key)}?owner=${list.owner}`, 'DELETE'); bench.note(`Deleted ${whose}`); }
  });
  const [text, setText] = useState('');
  const [every, setEvery] = useState<Cadence | ''>('');
  const [showDone, setShowDone] = useState(false);
  const [reminding, setReminding] = useState(false);
  const removeReminder = (r: ListReminder) => bench.confirm({
    title: `Remove the reminder "${r.label}"?`,
    body: `${r.recurring ? 'Every repeat of it goes. ' : ''}The list stays as it is. ${emcogName} knows you took the reminder off.`,
    action: 'Remove',
    run: async () => { await act(`/api/workspace/lists/reminders/${encodeURIComponent(r.id)}`, 'DELETE'); window.dispatchEvent(new Event('emcog:schedule-changed')); bench.note(`Removed the reminder ${r.label}`); }
  });
  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText(''); setEvery('');
    await act('/api/workspace/lists/item', 'POST', { text: t, list: list.display, owner: list.owner, ...(every ? { every } : {}) });
  };
  const moveTo = mine ? 'yours' : `${emcogName}'s`;
  const row = (i: ListRow) => (
    <div className={`ws-todo ws-todo-li ${i.done ? 'done' : ''}`} key={i.id}>
      <button className="ws-check" role="checkbox" aria-checked={i.done}
        aria-label={i.cadence ? (i.done ? `Un-tick this round of "${i.text}"` : `Tick off this round of "${i.text}"`) : (i.done ? `Put "${i.text}" back on the list` : `Check off "${i.text}"`)}
        onClick={() => act(`/api/workspace/lists/item/${i.id}/done`, 'POST', { done: !i.done })} />
      <span className="ws-todo-main">
        <span className="ws-todo-tt">{i.text}{i.priority === 'high' && <span className="ws-pill ws-pill-hi">High</span>}{i.due ? <span className="ws-todo-due">{new Date(i.due).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span> : null}</span>
        {i.cadence && (
          <span className="ws-todo-rhythm">
            <span aria-hidden="true">↻</span> {CADENCE_NAME[i.cadence]}
            {' · '}{i.lastDoneAt ? `last done ${dayWord(i.lastDoneAt)}` : 'not done yet'}
            {' · '}{i.done && i.nextAt ? `next ${dayWord(i.nextAt)}` : <b>due</b>}
          </span>
        )}
      </span>
      <button className="ws-todo-x ws-todo-move" aria-label={`Move "${i.text}" to ${moveTo}`} title={`Move to ${moveTo}`} onClick={() => act(`/api/workspace/lists/item/${i.id}/owner`, 'POST', { owner: mine ? 'person' : 'self' })}>⇄</button>
      <button className="ws-todo-x" aria-label={`Remove "${i.text}"`} title={i.cadence ? 'Retire this rhythm' : 'Remove'} onClick={() => act(`/api/workspace/lists/item/${i.id}`, 'DELETE')}>✕</button>
    </div>
  );
  const open = list.open.filter(i => !i.done).length;
  return (
    <div className="ws-listcard">
      <div className="ws-listcard-h"><h3>{title}</h3><span>{open} open</span><button className="ws-todo-x" aria-label={`Delete ${whose}`} title="Delete the list" onClick={removeList}>✕</button></div>
      {!mine && (
        <div className="ws-listcard-rems">
          {(list.reminders || []).map(r => (
            <span className="ws-pill ws-rem-chip" key={r.id} title={`Next: ${new Date(r.at).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`}>
              <span aria-hidden="true">⏰</span>{r.label}
              <button className="ws-rem-x" aria-label={`Remove the reminder ${r.label}`} title="Remove this reminder" onClick={() => removeReminder(r)}>✕</button>
            </span>
          ))}
          <button className="ws-linkbtn" onClick={() => setReminding(true)}>Remind me…</button>
        </div>
      )}
      {reminding && <ListReminderForm list={list} emcogName={emcogName} onClose={() => setReminding(false)} />}
      <div className="ws-todos">{list.open.length ? list.open.map(row) : <div className="ws-todo-empty">All clear.</div>}</div>
      <form className="ws-todo-add ws-todo-add-r" onSubmit={add}>
        <label className="ws-sr" htmlFor={`ws-add-${list.owner}-${list.key}`}>Add to {whose}</label>
        <input id={`ws-add-${list.owner}-${list.key}`} value={text} onChange={e => setText(e.target.value)} placeholder="Add an item…" autoComplete="off" />
        <label className="ws-sr" htmlFor={`ws-every-${list.owner}-${list.key}`}>How often</label>
        <select id={`ws-every-${list.owner}-${list.key}`} className="ws-select ws-todo-every" value={every} onChange={e => setEvery(e.target.value as Cadence | '')}>
          {CADENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </form>
      {list.done.length > 0 && (
        <div className="ws-todo-donebox">
          <button className="ws-linkbtn" onClick={() => setShowDone(s => !s)} aria-expanded={showDone}>{showDone ? 'Hide' : 'Show'} recently done ({list.done.length})</button>
          {showDone && <div className="ws-todos">{list.done.map(row)}</div>}
        </div>
      )}
    </div>
  );
};

export const ListsPlace: React.FC<{ emcogName: string }> = ({ emcogName }) => {
  const { token } = useAuth();
  const { data, error, reload } = useWorkspaceData<{ lists: TodoList[] }>('/api/workspace/lists', 30_000);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newItem, setNewItem] = useState('');
  const [newOwner, setNewOwner] = useState<Owner>('person');
  const [newEvery, setNewEvery] = useState<Cadence | ''>('');
  const [problem, setProblem] = useState<string | null>(null);
  const act = useCallback(async (path: string, method: string, body?: any) => {
    try {
      const res = await fetch(path, { method, headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || b.message || `That didn't work (${res.status}).`); }
      setProblem(null);
    } catch (e) { setProblem(e instanceof Error ? e.message : String(e)); }
    reload();
  }, [token, reload]);
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !newItem.trim()) { setProblem('A new list needs a name and its first item.'); return; }
    await act('/api/workspace/lists/item', 'POST', { text: newItem.trim(), list: newName.trim(), owner: newOwner, ...(newEvery ? { every: newEvery } : {}) });
    setCreating(false); setNewName(''); setNewItem(''); setNewOwner('person'); setNewEvery('');
  };
  const lists = data?.lists || [];
  const sides: Array<{ owner: Owner; title: string; empty: string }> = [
    { owner: 'person', title: 'Yours', empty: 'Nothing of yours on a list yet.' },
    { owner: 'self', title: `${emcogName}'s`, empty: `${emcogName} keeps no lists of their own yet — their own intentions and rhythms show up here.` }
  ];
  return (
    <div className="ws-wrap">
      <div className="ws-toolbar">
        <p className="ws-lede">Your lists, which {emcogName} keeps for you, and {emcogName}'s own. Tick things off, add to them, move an item to the other side — {emcogName} knows.</p>
        <button className="ws-btn" onClick={() => setCreating(c => !c)}>+ New list</button>
      </div>
      {creating && (
        <form className="ws-newlist" onSubmit={create}>
          <label htmlFor="ws-newlist-name">List name<input id="ws-newlist-name" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Groceries · Weekend · Work" autoComplete="off" /></label>
          <label htmlFor="ws-newlist-item">First item<input id="ws-newlist-item" value={newItem} onChange={e => setNewItem(e.target.value)} placeholder="Eggs" autoComplete="off" /></label>
          <label htmlFor="ws-newlist-owner">Whose<select id="ws-newlist-owner" className="ws-select" value={newOwner} onChange={e => setNewOwner(e.target.value as Owner)}><option value="person">Yours</option><option value="self">{emcogName}'s</option></select></label>
          <label htmlFor="ws-newlist-every">How often<select id="ws-newlist-every" className="ws-select" value={newEvery} onChange={e => setNewEvery(e.target.value as Cadence | '')}>{CADENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          <div className="ws-modal-acts"><button type="button" className="ws-btn" onClick={() => setCreating(false)}>Cancel</button><button type="submit" className="ws-btn ws-primary">Start the list</button></div>
        </form>
      )}
      {(problem || error) && <div className="ws-form-error" role="alert">{problem || error}</div>}
      {!error && data && sides.map(s => {
        const mine = lists.filter(l => l.owner === s.owner);
        const open = mine.reduce((n, l) => n + l.open.filter(i => !i.done).length, 0);
        return (
          <section className="ws-list-side" key={s.owner} aria-label={s.title}>
            <h2 className="ws-sec-h">{s.title}<span>{mine.length ? `${open} open` : ''}</span></h2>
            {mine.length
              ? <div className="ws-lists">{mine.map(l => <ListCard key={`${l.owner}:${l.key}`} list={l} emcogName={emcogName} act={act} />)}</div>
              : <div className="ws-todo-empty">{s.empty}</div>}
          </section>
        );
      })}
    </div>
  );
};

// ── Town ────────────────────────────────────────────────────────────────────
interface TownStatus { configured: boolean; connected: boolean; name: string | null; town: string; url: string }
interface TownWish { n: number; id: string; text: string; by: string[]; times: number; firstAt: number; lastAt: number; status: 'open' | 'built' | 'set-aside'; note?: string; closedAt?: number; examples: string[] }

/** 🌠 What residents set out to do in town that it had no way to do yet — the town's own list, most wanted first. */
const TownWishes: React.FC<{ wishes: TownWish[] | null; error: string | null; town: string }> = ({ wishes, error, town }) => {
  const [showClosed, setShowClosed] = useState(false);
  const open = (wishes || []).filter(w => w.status === 'open');
  const closed = (wishes || []).filter(w => w.status !== 'open');
  const item = (w: TownWish) => (
    <div key={w.id} className={`ws-wish ${w.status}`}>
      <div className="ws-wish-n" aria-label={`Number ${w.n}`}>{w.n}</div>
      <div className="ws-wish-main">
        <div className="ws-wish-what">{w.text}</div>
        <div className="ws-wish-meta">
          <span>{w.by.join(', ')}</span>
          <span>wished {w.times === 1 ? 'once' : `${w.times} times`}</span>
          <span>{w.lastAt !== w.firstAt ? `${when(w.firstAt)} – ${when(w.lastAt)}` : when(w.firstAt)}</span>
          {w.status === 'built' && <span className="ws-pill ws-pill-live">Built{w.closedAt ? ` ${when(w.closedAt)}` : ''}</span>}
          {w.status === 'set-aside' && <span className="ws-pill">Set aside</span>}
        </div>
        {w.note && <div className="ws-wish-note">{w.note}</div>}
        {w.status === 'open' && w.examples.length > 0 && <div className="ws-wish-ex" title={w.examples.join('\n')}>Went out meaning to: {w.examples[w.examples.length - 1]}</div>}
      </div>
    </div>
  );
  return (
    <div className="ws-wrap ws-wishes">
      <p className="ws-wish-intro">Things residents set out to do in {town} that the town had no way to do yet — what it could grow next. When one is built, mark it on the town's computer (<code>npm run wishes -- built &lt;number&gt;</code>) and everyone who wished for it hears about it.</p>
      {error && <div className="ws-form-error" role="alert">Couldn't read the wishes: {error}</div>}
      {wishes && !open.length && <div className="ws-empty">No open wishes — everything residents set out to do lately, the town had a way to do.</div>}
      <div className="ws-wish-list">{open.map(item)}</div>
      {closed.length > 0 && (
        <>
          <button className="ws-seed-past-btn" aria-expanded={showClosed} onClick={() => setShowClosed(v => !v)}>{showClosed ? '▾' : '▸'} Built and set aside · {closed.length}</button>
          {showClosed && <div className="ws-wish-list">{closed.map(item)}</div>}
        </>
      )}
    </div>
  );
};

export const TownPlace: React.FC<{ emcogName: string }> = ({ emcogName }) => {
  const { data } = useWorkspaceData<TownStatus>('/api/world/status', 30_000);
  const [view, setView] = useState<'town' | 'wishes'>('town');
  const wishes = useWorkspaceData<{ ok: boolean; wishes: TownWish[]; error?: string }>('/api/workspace/town/wishes', 120_000);
  const viewer = data?.url ? data.url.replace(/^ws(s?):\/\//, 'http$1://').replace(/\/resident\/?$/, '/') : '';
  const town = data?.town && data.town !== 'the town' ? data.town : 'The town';
  const status = !data ? 'Checking…' : !data.configured ? `${emcogName} doesn't have a home in town yet — connect one under Connections.` : data.connected ? `${emcogName} is connected and lives here as ${data.name || emcogName}.` : `${emcogName}'s home is here, but isn't connected right now.`;
  const openWishes = (wishes.data?.wishes || []).filter(w => w.status === 'open').length;
  const wishList = <TownWishes wishes={wishes.data?.wishes || (wishes.data ? [] : null)} error={wishes.error || (wishes.data && !wishes.data.ok ? wishes.data.error || 'no answer' : null)} town={town === 'The town' ? 'town' : town} />;
  const tabs = data?.configured && (
    <div className="ws-filters" role="group" aria-label="Town view">
      <button aria-pressed={view === 'town'} onClick={() => setView('town')}>Town</button>
      <button aria-pressed={view === 'wishes'} onClick={() => { setView('wishes'); wishes.reload(); }}>Wishes{openWishes ? ` · ${openWishes}` : ''}</button>
    </div>
  );
  // The town itself, right here — its own window is a click away for a bigger view. The window stays loaded while the
  // wishes are open, so coming back doesn't start the town over.
  if (viewer) return (
    <div className="ws-town-place">
      <div className="ws-town-bar">
        <span>{status}</span>
        <span className="ws-town-tools">{tabs}<a className="ws-btn" href={viewer} target="_blank" rel="noopener noreferrer" onClick={e => openLinkFromDesktop(e, true)}>Open in its own window</a></span>
      </div>
      <iframe className="ws-town-frame" hidden={view !== 'town'} src={viewer} title={town} allow="autoplay; fullscreen" />
      {view === 'wishes' && wishList}
    </div>
  );
  return (
    <div className="ws-wrap">
      <div className="ws-town">
        <div>
          <h1>{town}</h1>
          <p>{status}</p>
          {tabs}
        </div>
      </div>
      {view === 'wishes' && wishList}
    </div>
  );
};
